import React, { useEffect, useRef, useState } from 'react';
import "../css/ChatBot.css"
import notificationSound from '../assets/images/notification_alert.mp3';
import homeIconSrc from '../assets/images/home-icon.png';
import ChatCalendarPicker from './ChatCalendarPicker';

const ChatComponent = ({
  selectedMessages, 
  handleButtonClick, 
  handleInputSubmit, 
  showInput, 
  placeholder, 
  homeIcon,
  homeButtonDisabled = false,
  handleHomeButtonClick,
  handleKeyPress,
  setHomeIcon,
  userInput,
  setUserInput,
  setSelectedMessages,
  handleDateClick,
  handleCalendarSelect,
  currentFlow,
  inputDisabled = false,
  isIdentifierEntry = false,
  isOtpEntry = false,
  isAddressPincodeEntry = false,
  onChatScroll
}) => {
  const [inputTab, setInputTab] = useState('mobile');
  const endOfMessagesRef = useRef(null);
  const chatContainerRef = useRef(null);
  const timeoutRef = useRef(null);
  const hasRunRef = useRef(false);

  const audio = useRef(new Audio(notificationSound));
  const prevMessagesLength = useRef(selectedMessages.length);

  useEffect(() => {
    if (selectedMessages.length > prevMessagesLength.current) {
      const lastMessage = selectedMessages[selectedMessages.length - 1];
      if (lastMessage.type === 'server' && lastMessage.isType !== 'thinking') {
        audio.current.play().catch(error => console.error("Error playing audio:", error));
      }
    }
    prevMessagesLength.current = selectedMessages.length;
  }, [selectedMessages]);

  const renderTextWithLineBreaks = (text) => {
    if (text === undefined || text === null) {
      return null;
    }
    const safeText = typeof text === 'string' ? text : String(text);
    const lines = safeText.split('\n');
    return lines.map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < lines.length - 1 && <br />}
      </React.Fragment>
    ));
  };
  
  async function returnTime() {
    var date = new Date();
    var hours = date.getHours();
    var minutes = date.getMinutes();
    var ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    minutes = minutes < 10 ? '0' + minutes : minutes;
    var strTime = hours + ':' + minutes + ' ' + ampm;
    return strTime
  }

  useEffect(() => {
    if (endOfMessagesRef.current) {
      const scrollOptions = {
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest'
      };
  
      endOfMessagesRef.current.scrollIntoView(scrollOptions);
      setTimeout(() => {
        const chatContainer = endOfMessagesRef.current.closest('.chat-container');
        if (chatContainer) {
          chatContainer.scrollTop = chatContainer.scrollHeight;
          chatContainer.scrollTop += 70;
        }
      }, 100);
    }
  }, [selectedMessages]);

  useEffect(() => {
    if (!onChatScroll) return undefined;
    const node = chatContainerRef.current;
    if (!node) return undefined;

    const updateScrollBlur = () => {
      onChatScroll(node.scrollTop > 6);
    };

    updateScrollBlur();
    node.addEventListener('scroll', updateScrollBlur, { passive: true });
    return () => node.removeEventListener('scroll', updateScrollBlur);
  }, [onChatScroll, selectedMessages.length]);

  useEffect(() => {
      if (hasRunRef.current) return;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setSelectedMessages((prevMessages) => [
          ...prevMessages,
          { type: 'server', text: 'Hey! Are you still there? Please let us know how we can help. You can click on the Home icon to return to the main menu.', isType : 'text' },
        ]);
        setHomeIcon(true);
        hasRunRef.current = true;
      }, 60000);
      return () => clearTimeout(timeoutRef.current);
  }, [selectedMessages]);

  const XBeeMark = ({ className = "" }) => (
    <span className={`xbee-mark ${className}`.trim()} aria-hidden="true">
      <span className="xbee-mark-star xbee-mark-star-main">+</span>
      <span className="xbee-mark-star xbee-mark-star-left">+</span>
      <span className="xbee-mark-star xbee-mark-star-right">+</span>
      <span className="xbee-mark-dot xbee-mark-dot-1" />
      <span className="xbee-mark-dot xbee-mark-dot-2" />
      <span className="xbee-mark-dot xbee-mark-dot-3" />
      <span className="xbee-mark-dot xbee-mark-dot-4" />
      <span className="xbee-mark-dot xbee-mark-dot-5" />
    </span>
  );

  const renderChoiceLabel = (choice) => {
    const safeText = choice && choice.text != null ? String(choice.text) : '';
    return choice?.isType === 'date' ? safeText : renderTextWithLineBreaks(safeText);
  };

  const normalizeChoice = (value) => String(value || '').trim().toLowerCase();
  const compactChoice = (value) => normalizeChoice(value).replace(/[\s.]+/g, '');
  const choiceLabel = (button) => String(button?.text || button?.content || '');
  const isYesChoice = (value) => {
    const n = compactChoice(value);
    return n === 'yes' || n.startsWith('yes');
  };
  const isNoChoice = (value) => {
    const n = compactChoice(value);
    return n === 'no' || n.startsWith('no');
  };
  const isPromptLikeChoice = (value) => {
    const n = normalizeChoice(value);
    return n.includes('still have a query') || n.includes('how can we help you');
  };
  const isPrimaryCtaChoice = (value) => normalizeChoice(value) === 'back to previous menu';
  const isToggleChoiceGroup = (buttons) => {
    if (!Array.isArray(buttons) || buttons.length !== 2) return false;
    const labels = buttons.map((button) => choiceLabel(button));
    return labels.some(isYesChoice) && labels.some(isNoChoice);
  };
  const sortToggleButtons = (buttons) => (
    [...buttons].sort((left, right) => Number(isYesChoice(choiceLabel(left))) - Number(isYesChoice(choiceLabel(right))))
  );
  const isValidationMessage = (value) => {
    const n = normalizeChoice(value);
    return n.includes('pincode not found') || n.includes("couldn't match") || n.includes('delivery area');
  };
  const isSupportMessage = (value) => {
    const n = normalizeChoice(value);
    return (
      n.includes('unable to fetch')
      || n.includes('contact our support')
      || n.includes("couldn't find any shipments")
      || n.includes('for further assistance')
    );
  };
  const formatPhoneLinks = (html) => {
    if (!html) return html;
    return String(html).replace(
      /(<a\b[^>]*>[\s\S]*?<\/a>)|(?:\+91[\s-]?)?[6-9]\d{9}\b|0\d{2,4}[-\s]?\d{6,8}\b/gi,
      (full, existingAnchor) => {
        if (existingAnchor) return existingAnchor;
        const digits = full.replace(/\s|-/g, '');
        return `<a class="message-phone-link" href="tel:${digits}">${full}</a>`;
      }
    );
  };
  const isMenuPromptMessage = (value) => {
    const n = normalizeChoice(value);
    return (
      n.includes('what would you like to check')
      || n.includes('please select the date below')
    );
  }; 
  const formatServerHtml = (html) => {
    let next = String(html || '');
    if (isValidationMessage(next) && !next.includes('message-alert-title')) {
      const withoutHeading = next.replace(/PINCODE NOT FOUND[:.\s-]*/i, '').trim();
      next = `<span class="message-alert-title">PINCODE NOT FOUND</span>${withoutHeading || next}`;
    }
    if (isMenuPromptMessage(next) && !next.includes('message-menu-prompt')) {
      next = `<span class="message-menu-prompt">${next}</span>`;
    }
    next = formatTicketNumber(next);
    return formatPhoneLinks(next);
  };
  const formatTicketNumber = (html) => {
    let next = String(html || '');
    if (next.includes('ticket-number')) return next;
    next = next.replace(/created (the|a) ticket\s+"(\d+)"/gi, 'created $1 ticket <strong class="ticket-number">$2</strong>');
    next = next.replace(/created (the|a) ticket\s+(?!<)(\d+)/gi, 'created $1 ticket <strong class="ticket-number">$2</strong>');
    next = next.replace(/We've created a ticket\s+"(\d+)"/gi, 'We\'ve created a ticket <strong class="ticket-number">$1</strong>');
    next = next.replace(/We've created a ticket\s+(?!<)(\d+)/gi, 'We\'ve created a ticket <strong class="ticket-number">$1</strong>');
    next = next.replace(/ticket(?:\s+number)?\s+"(\d+)"/gi, 'ticket <strong class="ticket-number">$1</strong>');
    return next;
  };

  const groupedMessages = [];
  for (let index = 0; index < selectedMessages.length; index += 1) {
    const current = selectedMessages[index];
    if (
      current?.type === 'server'
      && (current?.isType === 'button' || current?.isType === 'date')
    ) {
      const buttons = [];
      while (
        index < selectedMessages.length
        && selectedMessages[index]?.type === 'server'
        && (selectedMessages[index]?.isType === 'button' || selectedMessages[index]?.isType === 'date')
      ) {
        const item = selectedMessages[index];
        if (item?.text) {
          buttons.push(item);
        }
        index += 1;
      }
      const promptButtons = buttons.filter((button) => isPromptLikeChoice(button.text));
      const actionButtons = buttons.filter((button) => !isPromptLikeChoice(button.text));
      promptButtons.forEach((button, promptIndex) => {
        groupedMessages.push({
          kind: 'message',
          value: { ...button, isType: 'text' },
          key: `prompt-${index}-${promptIndex}`,
        });
      });
      if (actionButtons.length) {
        const prev = groupedMessages[groupedMessages.length - 1];
        if (
          isToggleChoiceGroup(actionButtons)
          && prev
          && prev.kind === 'message'
          && prev.value?.type === 'server'
          && prev.value?.isType === 'text'
        ) {
          prev.inlineButtons = actionButtons;
        } else {
          groupedMessages.push({ kind: 'choice-group', buttons: actionButtons, key: `choices-${index}` });
        }
      }
      index -= 1;
      continue;
    }
    groupedMessages.push({ kind: 'message', value: current, key: `message-${index}` });
  }

  const chatEntries = [...groupedMessages];
  let lastUserIndex = -1;
  chatEntries.forEach((entry, entryIndex) => {
    if (entry?.kind === 'message' && entry.value?.type === 'user') {
      lastUserIndex = entryIndex;
    }
  });
  let dockedButtons = null;
  let dockedToggle = null;
  for (let entryIndex = chatEntries.length - 1; entryIndex >= 0; entryIndex -= 1) {
    const entry = chatEntries[entryIndex];
    const isUnanswered = entryIndex > lastUserIndex;
    if (entry?.kind === 'choice-group') {
      if (isToggleChoiceGroup(entry.buttons)) {
        if (isUnanswered && !dockedToggle) {
          dockedToggle = entry.buttons;
        }
      } else if (isUnanswered && !dockedButtons) {
        dockedButtons = entry.buttons;
      }
      chatEntries.splice(entryIndex, 1);
      if (entryIndex <= lastUserIndex) {
        lastUserIndex -= 1;
      }
      continue;
    }
    if (entry?.kind === 'message') {
      const inline = entry.inlineButtons || entry.value?.inlineChoices;
      if (Array.isArray(inline) && inline.length) {
        if (isToggleChoiceGroup(inline)) {
          if (isUnanswered && !dockedToggle) {
            dockedToggle = inline;
          }
        } else if (isUnanswered && !dockedButtons) {
          dockedButtons = inline;
        }
        entry.inlineButtons = undefined;
        if (entry.value) {
          entry.value = { ...entry.value, inlineChoices: undefined };
        }
      }
    }
  }

  if (dockedButtons && dockedButtons.length && !dockedToggle) {
    chatEntries.push({ kind: 'choice-group', buttons: dockedButtons, key: 'active-choices' });
    dockedButtons = null;
  }

  const isUserChatEntry = (entry) => (
    entry?.kind === 'message' && entry.value?.type === 'user'
  );

  const isServerChatEntry = (entry) => (
    entry?.kind === 'message'
    && entry.value?.type === 'server'
    && entry.value?.isType === 'text'
  );

  for (let entryIndex = 0; entryIndex < chatEntries.length; entryIndex += 1) {
    const entry = chatEntries[entryIndex];
    if (!isUserChatEntry(entry)) continue;

    const prevIsUser = entryIndex > 0 && isUserChatEntry(chatEntries[entryIndex - 1]);
    const nextIsUser = entryIndex < chatEntries.length - 1 && isUserChatEntry(chatEntries[entryIndex + 1]);

    if (!prevIsUser && !nextIsUser) {
      entry.userGroupPosition = 'single';
    } else if (!prevIsUser && nextIsUser) {
      entry.userGroupPosition = 'first';
    } else if (prevIsUser && nextIsUser) {
      entry.userGroupPosition = 'middle';
    } else {
      entry.userGroupPosition = 'last';
    }
  }

  for (let entryIndex = 0; entryIndex < chatEntries.length; entryIndex += 1) {
    const entry = chatEntries[entryIndex];
    if (!isServerChatEntry(entry)) continue;

    const prevIsServer = entryIndex > 0 && isServerChatEntry(chatEntries[entryIndex - 1]);
    const nextIsServer = entryIndex < chatEntries.length - 1 && isServerChatEntry(chatEntries[entryIndex + 1]);

    if (!prevIsServer && !nextIsServer) {
      entry.serverGroupPosition = 'single';
    } else if (!prevIsServer && nextIsServer) {
      entry.serverGroupPosition = 'first';
    } else if (prevIsServer && nextIsServer) {
      entry.serverGroupPosition = 'middle';
    } else {
      entry.serverGroupPosition = 'last';
    }
  }

  const renderChoiceChip = (button, buttonIndex, keyPrefix) => {
    const choiceText = button && (button.text != null ? String(button.text) : String(button.content || ''));
    if (!choiceText) return null;
    const onClick = button.isType === 'date'
      ? () => handleDateClick(choiceText)
      : () => handleButtonClick(choiceText);
    return (
      <button
        key={`${keyPrefix}-${buttonIndex}`}
        className={`chat-choice-chip ${isPrimaryCtaChoice(choiceText) ? 'chat-choice-chip-primary' : ''}`}
        onClick={onClick}
      >
        <span className="button-text">{renderChoiceLabel(button)}</span>
      </button>
    );
  };

  return (
    <>
      <div
        ref={chatContainerRef}
        className="chat-container chat-container-user-message-groups"
        style={{display: "flex", flexDirection : "column", width : "100%"}}
      >
        {chatEntries.map((entry, index) => {
          if (entry.kind === 'choice-group') {
            const isToggleGroup = isToggleChoiceGroup(entry.buttons);
            const renderedButtons = isToggleGroup ? sortToggleButtons(entry.buttons) : entry.buttons;
            return (
              <div key={entry.key} className={
                isToggleGroup
                  ? "xbee-toggle-group"
                  : "button-group button-group-wrap"
              }>
                {renderedButtons.map((button, buttonIndex) => {
                  const choiceText = button && button.text != null ? String(button.text) : '';
                  if (!choiceText) return null;
                  const onClick = button.isType === 'date'
                    ? () => handleDateClick(choiceText)
                    : () => handleButtonClick(choiceText);
                  return (
                    <button
                      key={`${entry.key}-${buttonIndex}`}
                      className={
                        isToggleGroup
                          ? `xbee-toggle-button ${isYesChoice(choiceText) ? 'xbee-toggle-button-primary' : 'xbee-toggle-button-secondary'}`
                          : `chat-choice-chip ${isPrimaryCtaChoice(choiceText) ? 'chat-choice-chip-primary' : ''}`
                      }
                      onClick={onClick}
                    >
                      <span className="button-text">{renderChoiceLabel(button)}</span>
                    </button>
                  );
                })}
              </div>
            );
          }

          const msg = entry.value;
          if (msg.type === 'user') {
            const userGroupPosition = entry.userGroupPosition || 'single';
            const showUserAvatar = userGroupPosition === 'single' || userGroupPosition === 'first';
            const showUserTimestamp = userGroupPosition === 'single' || userGroupPosition === 'last';

            return (
              <div
                key={entry.key}
                className={`message-row message-row-user message-row-user-${userGroupPosition}`.trim()}
              >
                <div className={`message-box user-message user-message-${userGroupPosition}`.trim()}>
                  <span className="message-text user-text">{msg.text}</span>
                </div>
                {showUserAvatar && (
                  <span className="message-avatar user-avatar" aria-hidden="true" />
                )}
                {showUserTimestamp && (
                  <div className="timer btnclasstimer user-timer">
                    {msg.timestamp}
                  </div>
                )}
              </div>
            );
          } else if (msg.type === 'server') {
            if (msg.isType === 'thinking') {
              return (
                <div key={entry.key} className="message-row message-row-server message-row-thinking">
                  <span className="message-avatar bot-avatar" aria-hidden="true" />
                  <div className="message-box server-message server-message-thinking" aria-label="XBee is typing">
                    <span className="xbee-thinking-dots" aria-hidden="true">
                      <span className="xbee-thinking-dot" />
                      <span className="xbee-thinking-dot" />
                      <span className="xbee-thinking-dot" />
                    </span>
                  </div>
                  <div className="timer btnclasstimer server-timer">
                    {msg.timestamp}
                  </div>
                </div>
              );
            }
            const safeMsgText = msg && msg.text != null ? String(msg.text) : '';
            if (msg.isType === 'text') {
              const inlineButtons = entry.inlineButtons || msg.inlineChoices;
              const hasInline = Array.isArray(inlineButtons) && inlineButtons.length > 0;
              const inlineIsToggle = hasInline && isToggleChoiceGroup(inlineButtons);
              const inlineRendered = hasInline
                ? (inlineIsToggle ? sortToggleButtons(inlineButtons) : inlineButtons)
                : [];
              const isMenuBubble = isMenuPromptMessage(safeMsgText);
              const serverGroupPosition = entry.serverGroupPosition || 'single';
              const showBotAvatar = serverGroupPosition === 'single' || serverGroupPosition === 'first';
              const showBotTimestamp = serverGroupPosition === 'single' || serverGroupPosition === 'last';
              return (
                <div
                  key={entry.key}
                  className={`message-row message-row-server message-row-server-${serverGroupPosition}`.trim()}
                >
                  {showBotAvatar && (
                    <span className="message-avatar bot-avatar" aria-hidden="true" />
                  )}
                  <div className={`message-box server-message server-message-${serverGroupPosition} ${isValidationMessage(safeMsgText) ? 'server-message-alert' : isSupportMessage(safeMsgText) ? 'server-message-support' : isMenuBubble ? 'server-message-menu' : ''}`.trim()}>
                    <span className="message-text" dangerouslySetInnerHTML={{ __html: formatServerHtml(safeMsgText) }}></span>
                    {hasInline && inlineIsToggle && (
                      <div className="xbee-toggle-group xbee-toggle-inline">
                        {inlineRendered.map((button, bi) => {
                          const ct = button && button.text != null ? String(button.text) : '';
                          if (!ct) return null;
                          return (
                            <button
                              key={`inline-${entry.key}-${bi}`}
                              className={`xbee-toggle-button ${isYesChoice(ct) ? 'xbee-toggle-button-primary' : 'xbee-toggle-button-secondary'}`}
                              onClick={() => handleButtonClick(ct)}
                            >
                              <span className="button-text">{renderChoiceLabel(button)}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {showBotTimestamp && (
                    <div className="timer btnclasstimer server-timer">
                      {msg.timestamp}
                    </div>
                  )}
                </div>
              );
            } else if (msg.isType === 'calendar' && msg.calendarActive && !msg.calendarLocked) {
              return (
                <div key={entry.key} className="button-group xbee-calendar-container">
                  <ChatCalendarPicker
                    disabled={Boolean(msg.calendarLocked)}
                    onSelect={(isoDate) => handleCalendarSelect && handleCalendarSelect(isoDate)}
                  />
                </div>
              );
            }
          } else if (Array.isArray(msg)) {
                const actionButtons = msg.filter((button) => !isPromptLikeChoice(button.content));
                const isToggleGroup = isToggleChoiceGroup(actionButtons);
                const renderedButtons = isToggleGroup ? sortToggleButtons(actionButtons) : actionButtons;
                return (
                  <div key={entry.key} className={isToggleGroup ? "xbee-toggle-group" : "button-group"}>
                  {renderedButtons.map((button, buttonIndex) => (
                    <button key={buttonIndex}
                      className={
                        isToggleGroup
                          ? `xbee-toggle-button ${isYesChoice(button.content) ? 'xbee-toggle-button-primary' : 'xbee-toggle-button-secondary'}`
                          : `chat-choice-chip ${isPrimaryCtaChoice(button.content) ? 'chat-choice-chip-primary' : ''}`
                      }
                      onClick={() => handleButtonClick(button.content)} >
                      <span className="button-text">{button.content}</span>
                    </button>
                  ))}
                </div>
              );
          }
          returnTime()
        })}

        <div ref={endOfMessagesRef} />
      </div>

      {dockedToggle && dockedToggle.length > 0 && (
        <div className="xbee-toggle-group xbee-toggle-dock">
          {sortToggleButtons(dockedToggle).map((button, buttonIndex) => {
            const choiceText = button && (button.text != null ? String(button.text) : String(button.content || ''));
            if (!choiceText) return null;
            return (
              <button
                key={`toggle-dock-${buttonIndex}`}
                className={`xbee-toggle-button ${isYesChoice(choiceText) ? 'xbee-toggle-button-primary' : 'xbee-toggle-button-secondary'}`}
                onClick={() => handleButtonClick(choiceText)}
              >
                <span className="button-text">{renderChoiceLabel(button)}</span>
              </button>
            );
          })}
        </div>
      )}

      {dockedButtons && dockedButtons.length > 0 && !dockedToggle && (
        <div className="button-group button-group-wrap">
          {dockedButtons.map((button, buttonIndex) => renderChoiceChip(button, buttonIndex, 'stack'))}
        </div>
      )}

      {showInput && isIdentifierEntry && (
        <div className="xbee-input-tab-group">
          <button
            className={`xbee-input-tab ${inputTab === 'mobile' ? 'xbee-input-tab-active' : ''}`}
            onClick={() => { setInputTab('mobile'); setUserInput(''); }}
          >
            Mobile Number
          </button>
          <button
            className={`xbee-input-tab ${inputTab === 'awb' ? 'xbee-input-tab-active' : ''}`}
            onClick={() => { setInputTab('awb'); setUserInput(''); }}
          >
            AWB Number
          </button>
        </div>
      )}

      {showInput && (
        <div className={`input-prompt ${userInput ? 'input-prompt-active' : ''}`}>
          <button
            type="button"
            className={`home-button${homeButtonDisabled ? ' home-button-disabled' : ''}`}
            onClick={() => handleHomeButtonClick()}
            disabled={homeButtonDisabled}
            aria-label="Go to home"
          >
            <img src={homeIconSrc} alt="" className="home-icon-img" />
          </button>
          <div className="composer-divider" aria-hidden="true" />
          <div className="chat-input-wrap">
            <input
              type="text"
              inputMode={
                (currentFlow === 'I want to change the address' || currentFlow.toLowerCase().includes('rider'))
                  && !isAddressPincodeEntry
                  ? "text"
                  : "numeric"
              }
              disabled={inputDisabled}
              value={userInput}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (isOtpEntry) {
                  if (/^\d{0,6}$/.test(inputValue)) {
                    setHomeIcon(false);
                    setUserInput(inputValue);
                  }
                } else if (isAddressPincodeEntry) {
                  if (/^\d{0,6}$/.test(inputValue)) {
                    setHomeIcon(false);
                    setUserInput(inputValue);
                  }
                } else if (currentFlow === 'I want to change the address' || currentFlow.toLowerCase().includes('rider')) {
                  setHomeIcon(false);
                  setUserInput(inputValue);
                } else if (isIdentifierEntry && inputTab === 'awb') {
                  if (/^\d*$/.test(inputValue)) {
                    setHomeIcon(false);
                    setUserInput(inputValue);
                  }
                } else if (isIdentifierEntry && inputTab === 'mobile') {
                  if (/^\d{0,10}$/.test(inputValue)) {
                    setHomeIcon(false);
                    setUserInput(inputValue);
                  }
                } else if (/^\d*$/.test(inputValue)) {
                  setHomeIcon(false);
                  setUserInput(inputValue);
                }
              }}
              maxLength={isOtpEntry ? 6 : undefined}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && isIdentifierEntry) {
                  const v = userInput.trim();
                  if (inputTab === 'mobile' && !/^[6-9]\d{9}$/.test(v)) {
                    e.preventDefault();
                    setSelectedMessages((prev) => [
                      ...prev,
                      { type: 'server', text: 'Please enter a valid 10-digit mobile number', isType: 'text' },
                    ]);
                    return;
                  }
                  if (inputTab === 'awb' && (v.length < 11 || v.length > 20)) {
                    e.preventDefault();
                    setSelectedMessages((prev) => [
                      ...prev,
                      { type: 'server', text: 'Please enter a valid AWB number (11–20 digits).', isType: 'text' },
                    ]);
                    return;
                  }
                }
                handleKeyPress(e);
              }}
              className="chat-input"
              placeholder={isIdentifierEntry ? (inputTab === 'mobile' ? 'Enter 10-digit mobile number' : 'Enter AWB number') : placeholder}
            />
          </div>
          {userInput && !inputDisabled ? (
            <label data-role="none" className="sendbtndiv send-halo" tabIndex="0">
              <button
                data-role="none"
                onClick={() => {
                  if (isIdentifierEntry) {
                    const v = userInput.trim();
                    if (inputTab === 'mobile') {
                      if (!/^[6-9]\d{9}$/.test(v)) {
                        setSelectedMessages((prev) => [
                          ...prev,
                          { type: 'server', text: 'Please enter a valid 10-digit mobile number', isType: 'text' },
                        ]);
                        return;
                      }
                    } else {
                      if (v.length < 11 || v.length > 20) {
                        setSelectedMessages((prev) => [
                          ...prev,
                          { type: 'server', text: 'Please enter a valid AWB number (11–20 digits).', isType: 'text' },
                        ]);
                        return;
                      }
                    }
                  }
                  handleInputSubmit();
                }}
                className="btndiv">
                <XBeeMark className="xbee-mark-send" />
              </button>
            </label>
          ) : (
            <span className="send-halo" aria-hidden="true">
              <span className="btndiv btndiv-static">
                <XBeeMark className="xbee-mark-send" />
              </span>
            </span>
          )}
        </div>
      )}
    </>
  );
};

export default ChatComponent;
